import React, { FormEvent, useEffect, useState } from 'react'
import { AxiosResponse } from 'axios'
import { useNavigate } from 'react-router'

import { updateSupplierRecoveryAssignedUser, toastSuccess } from '../../../api'
import { User } from '../../../models'
import { QueryParams } from '../../common/paginatedTable'
import { Loading } from '../../common'

interface AssignRecordsFormProps {
    queryParams: QueryParams
    getSupplierCount: (queryParams: QueryParams) => Promise<AxiosResponse<{ count: number }>>
    users: User[]
    selectedSuppliers: number[]
    setSelectedSuppliers: (selectedSuppliers: number[]) => void
    assignedUserId?: string
    onAssignedUserIdChange?: (userId: string) => void
}

export const AssignSuppliersRecoveryForm = (props: AssignRecordsFormProps) => {
    const {
        queryParams,
        getSupplierCount,
        users,
        assignedUserId: assignedUserIdProp,
        onAssignedUserIdChange,
    } = props
    const [internalAssignedUserId, setInternalAssignedUserId] = useState<string>('')
    const assignedUserId =
        assignedUserIdProp !== undefined ? assignedUserIdProp : internalAssignedUserId
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isFetchingSupplierCount, setIsFetchingSupplierCount] = useState(false)
    const [supplierCount, setSupplierCount] = useState<number | null>(null)

    const navigate = useNavigate()

    useEffect(() => {
        let isCancelled = false

        setIsFetchingSupplierCount(true)
        getSupplierCount(queryParams)
            .then((response) => {
                if (!isCancelled) {
                    setIsFetchingSupplierCount(false)
                    setSupplierCount(response.data.count)
                }
            })
            .catch((error) => {
                if (!isCancelled) {
                    setIsFetchingSupplierCount(false)
                }
            })

        return () => {
            isCancelled = true
        } 
    }, [queryParams, getSupplierCount])

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
    
        const countToAssign = getSupplierCountToAssign()
        if (!assignedUserId || countToAssign <= 0) {
            return
        }
    
        const params = {
            assigned_user_id: assignedUserId,
            selected_suppliers: props.selectedSuppliers,
            search: queryParams?.search,
        }
        setIsSubmitting(true)
    
        updateSupplierRecoveryAssignedUser(params)
            .then(() => {
                setIsSubmitting(false)
                props.setSelectedSuppliers([])
                toastSuccess('Suppliers Assigned')
                navigate(0)
            })
            .catch(() => {
                setIsSubmitting(false)
            })
    }
    
    const isFilteredByAssignedUser =
        queryParams?.assigned_user_id &&
        queryParams.assigned_user_id !== '-1' &&
        queryParams.assigned_user_id !== 'null'

    const getSupplierCountToAssign = () => {
        if (isFilteredByAssignedUser) {
            return 0
        }

        const assignedCount =
            props.users.find((user) => user.id === Number(assignedUserId))
                ?.assignedSupplierCount || 0
        const remainingCapacity = Math.max(0, 200 - assignedCount)

        if (props.selectedSuppliers.length > 0) {
            return Math.min(props.selectedSuppliers.length, remainingCapacity)
        }
        const unassignedCount = supplierCount ?? 0
        return Math.min(unassignedCount, remainingCapacity)
    }

    const assignedSuppliersCount =
        props.users.find((user) => user.id === Number(assignedUserId))
            ?.assignedSupplierCount || 0

    const remainingCapacity = Math.max(0, 200 - assignedSuppliersCount)

    const isOverCapacitySelection =
        props.selectedSuppliers.length > 0 &&
        props.selectedSuppliers.length > remainingCapacity

    const alertMessage = () => {
        if (!assignedUserId) return ''
        if (isOverCapacitySelection) {
            return 'Number of suppliers to assign is too many!'
        }
        return `${remainingCapacity} suppliers can be assigned to this user.`
    }

    return (
        <form onSubmit={(e) => handleSubmit(e)} className="d-flex">
            <div style={{ marginRight: '20px' }}>
                <label htmlFor="usersInput">Assign Suppliers to User</label>
                <select
                    className="form-select mb-2"
                    id="usersInput"
                    value={assignedUserId}
                    onChange={(e) => {
                        const value = e.target.value
                        if (onAssignedUserIdChange) {
                            onAssignedUserIdChange(value)
                        } else {
                            setInternalAssignedUserId(value)
                        }
                    }}
                    required
                >
                    <option value="">Select User</option>
                    {users.map((user) => (
                        <option key={`user_option_${user.id}`} value={user.id}>
                            {user.name}
                        </option>
                    ))}
                </select>
                {assignedUserId && (
                    <h6
                        className={isOverCapacitySelection ? 'text-danger' : 'text-muted'}
                        role={isOverCapacitySelection ? 'alert' : undefined}
                    >
                        {alertMessage()}
                    </h6>
                )}
            </div>
            <div>
                <button
                    className="btn btn-primary"
                    style={{ marginTop: '22px' }}
                    disabled={
                        isSubmitting ||
                        !assignedUserId ||
                        isOverCapacitySelection ||
                        getSupplierCountToAssign() <= 0
                    }
                    type="submit"
                >
                    Assign {getSupplierCountToAssign()} of{' '}
                    {isFetchingSupplierCount ? <Loading sm={true} /> : supplierCount}{' '}
                    unassigned {isSubmitting && <Loading sm={true} />}
                </button>
            </div>
        </form>
    )
}
